import { useMemo } from 'react';
import clsx from 'clsx';

import { capitalize, css, styled } from '@mui/material';

import { spinnerClasses } from './theme';
import { SpinnerSizeMap } from './types';

import type { Theme } from '../../types';
import type { SpinnerOwnerState, SpinnerProps } from './types';

const Spinner = (props: SpinnerProps) => {
  const {
    size = 'sm', // 기본값 설정 (theme defaultProps에서 이동)
    color = 'default', // 기본값 설정 (theme defaultProps에서 이동)
    className,
  } = props;

  const ownerState: SpinnerOwnerState = {
    ...props,
    size,
    color,
  };

  const getDynamicClasses = () => {
    return {
      root: clsx(
        size && spinnerClasses[`size${capitalize(size)}` as keyof typeof spinnerClasses],
        color && spinnerClasses[`color${capitalize(color)}` as keyof typeof spinnerClasses],
        className,
      ),
    };
  };

  const dynamicClasses = getDynamicClasses();

  const gradientFirstId = useMemo(() => `gradient-1-${color}-${size}`, [color, size]);
  const gradientSecondId = useMemo(() => `gradient-2-${color}-${size}`, [color, size]);

  return (
    <SpinnerRoot ownerState={ownerState} className={dynamicClasses.root}>
      <SpinnerBase viewBox="-5 -5 30 30" ownerState={ownerState}>
        <defs>
          <linearGradient x1="0%" y1="0%" x2="100%" y2="0" id={gradientFirstId}>
            <stop stopColor="currentColor" offset="0%" />
            <stop stopColor="currentColor" offset="63.1%" stopOpacity=".631" />
            <stop stopColor="currentColor" offset="100%" stopOpacity=".5" />
          </linearGradient>
          <linearGradient x1="0%" y1="0%" x2="100%" y2="0" id={gradientSecondId}>
            <stop stopColor="currentColor" offset="0%" stopOpacity=".5" />
            <stop stopColor="currentColor" offset="63.1%" stopOpacity=".12" />
            <stop stopColor="currentColor" offset="100%" stopOpacity="0" />
          </linearGradient>
        </defs>
        <g fill="none">
          <g transform="translate(1 1)">
            <path
              d="M 10.5 10.5 m -10.5 0a 10.5 10.5 0 1 0 21 0a"
              stroke={`url(#${gradientFirstId})`}
            />
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 10.5 10.5"
              to="360 10.5 10.5"
              dur="1s"
              repeatCount="indefinite"
            />
          </g>
          <g transform="translate(1 1)">
            <path
              d="M 10.5 10.5 m -10.5 0a 10.5 10.5 0 1 0 21 0a"
              stroke={`url(#${gradientSecondId})`}
            />
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="-180 10.5 10.5"
              to="180 10.5 10.5"
              dur="1s"
              repeatCount="indefinite"
            />
          </g>
        </g>
      </SpinnerBase>
    </SpinnerRoot>
  );
};

const SpinnerRoot = styled('div', {
  target: spinnerClasses.root,
})<{ ownerState: SpinnerOwnerState }>(({ ownerState, theme }) => {
  const { color, size } = ownerState;

  return css`
    ${getColorStyles(color, theme)}
    ${getSizeStyles(size, theme)}
  `;
});

const SpinnerBase = styled('svg', {
  target: spinnerClasses.spinner,
})<{ ownerState: SpinnerOwnerState }>``;

// Style helper functions
const getColorStyles = (color: SpinnerOwnerState['color'], _theme: Theme) => {
  const colorStylesMap = {
    default: css`
      color: #13161999;
    `,
    inverse: css`
      color: #f6f7f9;
    `,
  };

  return colorStylesMap[color];
};

const getSizeStyles = (size: SpinnerOwnerState['size'], _theme: Theme) => {
  const sizeStylesMap = {
    xs: css`
      width: ${SpinnerSizeMap['xs']}px;
      height: ${SpinnerSizeMap['xs']}px;

      & path {
        stroke-width: 5;
      }
    `,
    sm: css`
      width: ${SpinnerSizeMap['sm']}px;
      height: ${SpinnerSizeMap['sm']}px;

      & path {
        stroke-width: 5;
      }
    `,
    md: css`
      width: ${SpinnerSizeMap['md']}px;
      height: ${SpinnerSizeMap['md']}px;

      & path {
        stroke-width: 4;
      }
    `,
    lg: css`
      width: ${SpinnerSizeMap['lg']}px;
      height: ${SpinnerSizeMap['lg']}px;

      & path {
        stroke-width: 4;
      }
    `,
  };

  return sizeStylesMap[size];
};

export default Spinner;
